import api from '../lib/axios';
import type { ApiResponse } from '@maios/shared';

// --- Types ---

export interface TestCaseListItem {
  id: string;
  tcId: string;
  projectId: string;
  specId: string;
  title: string;
  type: 'POSITIVE' | 'NEGATIVE' | 'BOUNDARY' | 'ALTERNATIVE';
  precondition: string | null;
  steps: { step: number; action: string; input?: string; expected?: string }[];
  expectedResult: string | null;
  priority: string;
  createdAt: string;
  updatedAt: string;
  specification: { id: string; functionCode: string; title: string } | null;
  needsReview: boolean;
  reviewedBy: string | null;
  reviewedAt: string | null;
  reviewer: { id: string; name: string; email: string } | null;
  linkedIssues: { id: string; issueId: string; title: string; status: string }[];
  _count: { linkedIssues: number; executions: number };
}

export interface TestEnvSnapshotEntry {
  id: string;
  profileName: string;
  os?: string;
  osVersion?: string;
  browser?: string;
  browserVersion?: string;
  device?: string;
  screenResolution?: string;
  additionalInfo?: string;
}

export interface TestExecution {
  id: string;
  testCaseId: string;
  status: string;
  actualResult: string | null;
  executedBy: string;
  executedAt: string;
  testEnvSnapshot: TestEnvSnapshotEntry[] | null;
  notes: string | null;
  executor: { id: string; name: string; email: string; avatarUrl: string | null };
}

export interface ExecuteTestCaseData {
  status: 'PASS' | 'FAIL' | 'BLOCKED' | 'SKIP';
  actualResult?: string;
  notes?: string;
}

// CHG-008: TC 수동 생성 데이터
export interface CreateTestCaseData {
  specId?: string;
  title: string;
  type: 'POSITIVE' | 'NEGATIVE' | 'BOUNDARY' | 'ALTERNATIVE';
  priority: 'BLOCKER' | 'CRITICAL' | 'HIGH' | 'MEDIUM' | 'LOW';
  precondition?: string;
  steps: { step: number; action: string; expected?: string }[];
  expectedResult: string;
}

// CHG-010: TC 수정 데이터
export interface UpdateTestCaseData {
  title?: string;
  type?: 'POSITIVE' | 'NEGATIVE' | 'BOUNDARY' | 'ALTERNATIVE';
  precondition?: string | null;
  steps?: { step: number; action: string; expected?: string }[];
  expectedResult?: string | null;
  priority?: 'BLOCKER' | 'CRITICAL' | 'HIGH' | 'MEDIUM' | 'LOW';
}

// --- API calls ---

export async function fetchTestCases(
  projectId: string,
  params?: { specId?: string; status?: string; type?: string },
) {
  const query = new URLSearchParams();
  if (params?.specId) query.set('specId', params.specId);
  if (params?.status) query.set('status', params.status);
  if (params?.type) query.set('type', params.type);
  const qs = query.toString();
  const res = await api.get<ApiResponse<TestCaseListItem[]>>(
    `/projects/${projectId}/testcases${qs ? `?${qs}` : ''}`,
  );
  return res.data.data!;
}

export async function fetchTestCase(projectId: string, testCaseId: string) {
  const res = await api.get<ApiResponse<TestCaseListItem>>(
    `/projects/${projectId}/testcases/${testCaseId}`,
  );
  return res.data.data!;
}

export async function executeTestCase(
  projectId: string,
  testCaseId: string,
  data: ExecuteTestCaseData,
) {
  const res = await api.post<ApiResponse<TestExecution>>(
    `/projects/${projectId}/testcases/${testCaseId}/execute`,
    data,
  );
  return res.data.data!;
}

export async function fetchExecutionHistory(projectId: string, testCaseId: string) {
  const res = await api.get<ApiResponse<TestExecution[]>>(
    `/projects/${projectId}/testcases/${testCaseId}/history`,
  );
  return res.data.data!;
}

// CHG-008: TC 수동 생성
export async function createTestCase(projectId: string, data: CreateTestCaseData) {
  const res = await api.post<ApiResponse<TestCaseListItem>>(
    `/projects/${projectId}/testcases`,
    data,
  );
  return res.data.data!;
}

// CHG-010: TC 수정
export async function updateTestCase(projectId: string, testCaseId: string, data: UpdateTestCaseData) {
  const res = await api.put<ApiResponse<TestCaseListItem>>(
    `/projects/${projectId}/testcases/${testCaseId}`,
    data,
  );
  return res.data.data!;
}

// TC 삭제
export async function deleteTestCase(projectId: string, testCaseId: string) {
  await api.delete(`/projects/${projectId}/testcases/${testCaseId}`);
}

// TC 명세 간 이동
export interface MoveTestCasesResult {
  movedCount: number;
  warnings: string[];
}

export async function moveTestCases(
  projectId: string,
  data: { testCaseIds: string[]; targetSpecId: string },
) {
  const res = await api.post<ApiResponse<MoveTestCasesResult>>(
    `/projects/${projectId}/testcases/move`,
    data,
  );
  return res.data.data!;
}

// CHG-021: TC 검토완료
export async function reviewComplete(projectId: string, testCaseId: string) {
  const res = await api.patch<ApiResponse<TestCaseListItem>>(
    `/projects/${projectId}/testcases/${testCaseId}/review-complete`,
  );
  return res.data.data!;
}

// CHG-021: TC 일괄 검토완료
export async function batchReviewComplete(projectId: string, testCaseIds: string[]) {
  const res = await api.patch<ApiResponse<{ message: string; updatedCount: number }>>(
    `/projects/${projectId}/testcases/batch-review-complete`,
    { testCaseIds },
  );
  return res.data.data!;
}

// CHG-022: 재검토 필요 TC 그룹핑
export interface NeedsReviewGroup {
  specId: string;
  specTitle: string;
  functionCode: string;
  testCaseCount: number;
  testCaseIds: string[];
  testCases: { testCaseId: string; title: string; testCaseKey: string }[];
  latestVersion: number;
  previousVersion: number;
  lastChangedBy: { id: string; name: string } | null;
  lastChangedAt: string | null;
}

export interface NeedsReviewSummary {
  totalCount: number;
  groups: NeedsReviewGroup[];
}

export async function fetchNeedsReviewSummary(projectId: string) {
  const res = await api.get<ApiResponse<NeedsReviewSummary>>(
    `/projects/${projectId}/testcases/needs-review-summary`,
  );
  return res.data.data!;
}

// --- Test Env Profile types & API ---

export interface TestEnvProfileItem {
  id: string;
  userId: string;
  profileName: string;
  os: string | null;
  osVersion: string | null;
  browser: string | null;
  browserVersion: string | null;
  device: string | null;
  screenResolution: string | null;
  additionalInfo: string | null;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface CreateTestEnvProfileData {
  profileName: string;
  os?: string;
  osVersion?: string;
  browser?: string;
  browserVersion?: string;
  device?: string;
  screenResolution?: string;
  additionalInfo?: string;
}

export interface UpdateTestEnvProfileData {
  profileName?: string;
  os?: string | null;
  osVersion?: string | null;
  browser?: string | null;
  browserVersion?: string | null;
  device?: string | null;
  screenResolution?: string | null;
  additionalInfo?: string | null;
  isActive?: boolean;
}

export async function fetchTestEnvProfiles(userId: string) {
  const res = await api.get<ApiResponse<TestEnvProfileItem[]>>(
    `/users/${userId}/test-env-profiles`,
  );
  return res.data.data!;
}

export async function createTestEnvProfile(userId: string, data: CreateTestEnvProfileData) {
  const res = await api.post<ApiResponse<TestEnvProfileItem>>(
    `/users/${userId}/test-env-profiles`,
    data,
  );
  return res.data.data!;
}

export async function updateTestEnvProfile(
  userId: string,
  profileId: string,
  data: UpdateTestEnvProfileData,
) {
  const res = await api.put<ApiResponse<TestEnvProfileItem>>(
    `/users/${userId}/test-env-profiles/${profileId}`,
    data,
  );
  return res.data.data!;
}

export async function deleteTestEnvProfile(userId: string, profileId: string) {
  await api.delete(`/users/${userId}/test-env-profiles/${profileId}`);
}

export async function setActiveProfiles(userId: string, profileIds: string[]) {
  const res = await api.put<ApiResponse<TestEnvProfileItem[]>>(
    `/users/${userId}/test-env-profiles/active`,
    { profileIds },
  );
  return res.data.data!;
}
